// ============================================================================
// utils_dragReorder.ts — glisser-déposer de réordonnancement, régie
//
// Quatre zones de la régie se réordonnent au glisser : les mini-vues du
// multiview, les sources du deck, la rangée « Preview source » et les boutons
// média / scène. Elles partagent ce mécanisme pour une raison précise : la
// FAMILLE porte le type MIME du transfert, et le navigateur refuse alors de
// déposer un bouton média sur une source vidéo. Sans ça, chaque zone devrait
// vérifier elle-même la provenance de ce qu'on lui lâche dessus, et la première
// qui l'oublierait échangerait deux tableaux sans rapport.
// ============================================================================

/**
 * Familles de glisser-déposer. Deux éléments ne peuvent être échangés que s'ils
 * appartiennent à la même — le type MIME du transfert le garantit.
 *
 * `slot` couvre à la fois les sources du deck, la rangée Preview et les
 * mini-vues du multiview : ce sont trois vues du MÊME tableau d'emplacements,
 * et se glisser de l'une à l'autre a un sens.
 */
export type ReorderFamily = 'slot' | 'media' | 'scene';

export const reorderMime = (family: ReorderFamily): string => `application/x-kleon-${family}`;

/**
 * Échange deux éléments. Renvoie le tableau d'origine — même référence — quand
 * il n'y a rien à faire : les appelants s'en servent dans des updaters `setState`
 * où renvoyer une nouvelle référence pour rien déclencherait un rendu, et parfois
 * un effet qui réécrirait ce qu'on vient de poser.
 */
export const swapAt = <T,>(list: T[], from: number, to: number): T[] => {
  if (from === to) return list;
  if (from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
  const next = [...list];
  [ next[from] , next[to] ] = [ next[to] , next[from] ];
  return next;
};

export interface ReorderDragOptions {
  family : ReorderFamily;
  index  : number;
  /**
   * Un emplacement VIDE reste une cible de dépôt valable — y déposer revient à
   * déplacer — mais il n'y a rien à en tirer.
   */
  canDrag: boolean;
  onReorder: (fromIndex: number, toIndex: number) => void;
  /** Retour visuel : élément saisi, élément survolé. Les deux à null au repos. */
  onDragStateChange?: (dragged: number | null, over: number | null) => void;
}

export interface ReorderDragHandlers {
  draggable  : boolean;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd  : () => void;
  onDragOver : (e: React.DragEvent) => void;
  onDragEnter: (e: React.DragEvent) => void;
  onDrop     : (e: React.DragEvent) => void;
}

/**
 * Fabrique les gestionnaires d'un élément réordonnable.
 *
 * Le porte-données ne transporte que l'index : c'est la seule donnée nécessaire,
 * et le type MIME fait le reste du travail — un glisser venu d'ailleurs (fichier,
 * lien, autre famille) n'a pas ce format et ne passera aucun des tests.
 */
export const makeReorderDragHandlers = ({
  family, index, canDrag, onReorder, onDragStateChange,
}: ReorderDragOptions): ReorderDragHandlers => {
  const mime = reorderMime(family);

  return {
    draggable  : canDrag,
    onDragStart: (e: React.DragEvent) => {
      if (!canDrag) return;
      e.dataTransfer.setData(mime, String(index));
      e.dataTransfer.effectAllowed = 'move';
      onDragStateChange?.(index, null);
    },
    onDragEnd  : () => onDragStateChange?.(null, null),
    onDragOver : (e: React.DragEvent) => {
      if (!e.dataTransfer.types.includes(mime)) return;
      // preventDefault à CHAQUE survol : sans lui, le navigateur refuse le dépôt.
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    },
    onDragEnter: (e: React.DragEvent) => {
      if (!e.dataTransfer.types.includes(mime)) return;
      onDragStateChange?.(null, index);
    },
    onDrop     : (e: React.DragEvent) => {
      const raw = e.dataTransfer.getData(mime);
      if (raw === '') return;
      e.preventDefault();
      e.stopPropagation();
      const from = Number(raw);
      onDragStateChange?.(null, null);
      if (Number.isInteger(from) && from !== index) onReorder(from, index);
    },
  };
};
